import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useV2Auth } from '@/context/V2AuthContext';
import { useTranslation } from 'react-i18next';
import { Heart, Send, CalendarDays, Star, Sparkles, Check, ListChecks, HeartHandshake } from 'lucide-react';

export default function PublicProfileV2({ username }) {
  const { user, API_V2 } = useV2Auth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setNotFound(false);
      try {
        const res = await axios.get(`${API_V2}/public-profile/${encodeURIComponent(username)}`);
        if (!cancelled) setProfile(res.data);
      } catch (_e) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [API_V2, username]);

  const sendCarma = () => {
    if (!user) { navigate('/auth'); return; }
    navigate('/give', {
      state: {
        preselectUser: {
          id: profile.id,
          name: profile.name,
          initial: (profile.name || '?')[0].toUpperCase(),
        },
      },
    });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-sm text-[var(--muted)]">{t('common.loading')}</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-8 shadow-sm text-center space-y-4 max-w-md" data-testid="public-profile-not-found">
          <p className="text-sm text-[var(--muted)]">{t('publicProfile.notFound')}</p>
          <button type="button" onClick={() => navigate('/')} className="rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] px-5 py-2 text-sm hover:opacity-90">
            {t('publicProfile.toHome')}
          </button>
        </div>
      </div>
    );
  }

  const memberDate = profile.memberSince
    ? new Date(profile.memberSince).toLocaleDateString('de-DE', { year: 'numeric', month: 'long' })
    : '';

  return (
    <div className="max-w-2xl mx-auto px-4 py-10 space-y-6" data-testid="public-profile-page">
      {/* Header card */}
      <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-8 shadow-sm text-center space-y-4">
        <div className="w-24 h-24 mx-auto rounded-full bg-[var(--primary)]/10 flex items-center justify-center overflow-hidden">
          {profile.hasPhoto ? (
            <img src={`${API_V2}/profile/photo/${profile.id}?v=${encodeURIComponent(profile.photoVersion || '')}`} alt={profile.name} className="w-full h-full object-cover" data-testid="public-profile-photo" />
          ) : (
            <span className="text-3xl font-semibold text-[var(--primary)]">{(profile.name || '?')[0].toUpperCase()}</span>
          )}
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-[var(--text)]" data-testid="public-profile-name">{profile.name}</h1>
          <p className="text-sm text-[var(--muted)]">@{profile.username}</p>
        </div>
        {profile.personalMessage && (
          <p className="text-sm italic text-[var(--text)]/80 max-w-md mx-auto leading-relaxed" data-testid="public-profile-message">„{profile.personalMessage}"</p>
        )}
        {memberDate && (
          <p className="text-xs text-[var(--muted)] flex items-center justify-center gap-1">
            <CalendarDays className="w-3.5 h-3.5" />
            {t('publicProfile.memberSince', { date: memberDate })}
          </p>
        )}
        {/* PHASE 0: Öffentliche Carma-Zähler geparkt ("Menschen werden nicht bewertet")
        <div className="flex justify-center gap-8 pt-2">
          <div>
            <div className="text-xl font-semibold text-[var(--primary)]" data-testid="public-profile-received">{profile.receivedCount}</div>
            <div className="text-xs text-[var(--muted)]">{t('publicProfile.receivedCarma')}</div>
          </div>
          <div>
            <div className="text-xl font-semibold text-[var(--text)]">{profile.sentCount}</div>
            <div className="text-xs text-[var(--muted)]">{t('publicProfile.sentCarma')}</div>
          </div>
        </div> */}
        <button
          type="button"
          onClick={sendCarma}
          data-testid="public-profile-send-carma-btn"
          className="inline-flex items-center gap-2 rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] px-6 py-2.5 text-sm font-medium hover:opacity-90"
        >
          <Send className="w-4 h-4" />
          {t('publicProfile.sendCarma', { name: profile.name })}
        </button>
      </div>

      {/* Pinned Carma — what others appreciate */}
      {profile.pinnedCarma?.length > 0 && (
        <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-6 shadow-sm space-y-4" data-testid="public-profile-pinned-carma">
          <h2 className="text-base font-semibold text-[var(--text)] flex items-center gap-2">
            <Star className="w-4 h-4 text-[var(--primary)]" />
            {t('innerstes.pinnedCarmaPublicTitle', { name: profile.name })}
          </h2>
          <div className="space-y-3">
            {profile.pinnedCarma.map((c) => (
              <div key={c.id} className="border border-[var(--border)] rounded-xl bg-white/70 p-4">
                <p className="text-sm leading-relaxed text-[var(--text)] italic">„{c.message}"</p>
                <p className="text-[11px] text-[var(--muted)] mt-2">— {c.anonymous || !c.senderName ? t('innerstes.anonymous') : c.senderName}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pinned impulses */}
      {profile.pinnedImpulses?.length > 0 && (
        <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-6 shadow-sm space-y-4" data-testid="public-profile-pinned-impulses">
          <h2 className="text-base font-semibold text-[var(--text)] flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[var(--primary)]" />
            {t('innerstes.pinnedImpulsesPublicTitle', { name: profile.name })}
          </h2>
          <div className="space-y-3">
            {profile.pinnedImpulses.map((imp) => (
              <div key={imp.id} className="border border-[var(--border)] rounded-xl bg-white/70 p-4">
                <p className="text-sm leading-relaxed text-[var(--text)]">
                  {imp.type === 'gratitude'
                    ? (<><span className="text-[var(--primary)] font-medium">Ich bin dankbar {imp.prefix || 'für'}</span> {imp.text}</>)
                    : imp.text}
                </p>
                {imp.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {imp.tags.map((tag) => (
                      <span key={tag} className="text-[10px] rounded-full bg-[var(--primary)]/10 text-[var(--primary)] px-2 py-0.5">#{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lebensliste */}
      {profile.lebensliste?.length > 0 && (
        <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-6 shadow-sm space-y-4" data-testid="public-profile-lebensliste">
          <h2 className="text-base font-semibold text-[var(--text)] flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-[var(--primary)]" />
            {t('lebensliste.publicTitle', { name: profile.name })}
          </h2>
          <div className="space-y-2">
            {profile.lebensliste.map((e) => (
              <div key={e.id} className="flex items-center gap-3 border border-[var(--border)] rounded-xl bg-white/70 px-4 py-3">
                <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${e.done ? 'bg-[var(--primary)] text-white' : 'border border-[var(--border)]'}`}>
                  {e.done && <Check className="w-3 h-3" />}
                </span>
                <span className={`text-sm flex-1 leading-relaxed ${e.done ? 'line-through text-[var(--muted)]' : 'text-[var(--text)]'}`}>{e.text}</span>
                <span className="text-[10px] rounded-full bg-[var(--primary)]/10 text-[var(--primary)] px-2 py-0.5 flex-shrink-0">{t(`lebensliste.cat.${e.category}`)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Herzensmenschen */}
      {profile.herzensmenschen?.length > 0 && (
        <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-6 shadow-sm space-y-4" data-testid="public-profile-herzensmenschen">
          <h2 className="text-base font-semibold text-[var(--text)] flex items-center gap-2">
            <HeartHandshake className="w-4 h-4 text-[var(--primary)]" />
            {t('herzensmenschen.publicTitle', { name: profile.name })}
          </h2>
          <div className="flex flex-wrap gap-2">
            {profile.herzensmenschen.map((h) => (
              <div key={h.id} className="inline-flex items-center gap-2 border border-[var(--border)] rounded-full bg-white/70 pl-1.5 pr-3 py-1.5">
                <span className="w-6 h-6 rounded-full bg-[var(--primary)]/10 text-[var(--primary)] flex items-center justify-center text-[11px] font-semibold">{(h.name || '?')[0].toUpperCase()}</span>
                <span className="text-xs font-medium text-[var(--text)]">{h.name}</span>
                {h.relation && <span className="text-[10px] text-[var(--muted)]">· {h.relation}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gratitude impulses */}
      {profile.gratitude?.length > 0 && (
        <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-6 shadow-sm space-y-4" data-testid="public-profile-gratitude">
          <h2 className="text-base font-semibold text-[var(--text)] flex items-center gap-2">
            <Heart className="w-4 h-4 text-[var(--primary)]" />
            {t('publicProfile.gratitudeTitle', { name: profile.name })}
          </h2>
          <div className="space-y-3">
            {profile.gratitude.map((imp) => (
              <div key={imp.id} className="border border-[var(--border)] rounded-xl bg-white/70 p-4">
                <p className="text-sm leading-relaxed text-[var(--text)]">
                  <span className="text-[var(--primary)] font-medium">Ich bin dankbar {imp.prefix || 'für'}</span> {imp.text}
                </p>
                {imp.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {imp.tags.map((tag) => (
                      <span key={tag} className="text-[10px] rounded-full bg-[var(--primary)]/10 text-[var(--primary)] px-2 py-0.5">#{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
